import React, {MouseEvent, useRef} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {BrandMapPinIcon, BrandPropertyPlanIcon, GalleryOutlineIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {UnlockChoiceCtaRequest, useUnlockChoiceCta} from "../../auth/hooks/use_unlock_choice_cta";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {isPropertyFavouriteButtonAvailable} from "../utils/is_property_favourite_button_available";

interface IProps {
    offer: IPropertyDetailOffer;
    property?: IPropertyDetail;
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    className?: string;
    navBarContentStyle?: string;
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyNavBar = React.memo((props: IProps) => {
    const {property, offer, onButtonClick} = props;

    const {isMobile} = useUserDevice();

    const wrapper = useRef<HTMLTableSectionElement>(null);
    const isNavBarSticky = useStickyIntersection(wrapper);

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const planImage = head(property?.plan_image_pages || [])?.image_url;

    const {isInterceptable, startUnlockChoice} = useUnlockChoiceCta();
    const planUnlockChoiceRequest: UnlockChoiceCtaRequest = {kind: "floor_plan", propertyId: property?.id};
    const shouldOpenPlanUnlockChoice = !planImage && isInterceptable(planUnlockChoiceRequest);

    const onShowPropertyGalleryClick = () => {
        props.onButtonClick(OfferModalActiveTab.GALLERY, GTMEventApplicationSourceSection.BUTTON);
    };

    const onShowPropertyPlanClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_DETAIL, offer, offer.vendor);
        if (property) {
            trackAmplitudeButtonClick({
                buttonName: "view_floor_plan",
                value: "see_floor_plan",
                buttonCategory: "property_page",
                itemId: offer.id,
                propertyId: property.id
            });
        }
        props.onButtonClick(
            OfferModalActiveTab.PLAN,
            isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
        );
    };

    const onPlanDownloadClick = (event: MouseEvent<HTMLElement>) => {
        if (startUnlockChoice(planUnlockChoiceRequest)) {
            event.preventDefault();
        }
    };

    const onShowMapClick = () => {
        if (property) {
            trackAmplitudeButtonClick({
                buttonName: "view_map",
                value: "see_on_map",
                buttonCategory: "property_page",
                itemId: offer.id,
                propertyId: property.id
            });
        }
        onButtonClick(OfferModalActiveTab.MAP, isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON);
    };

    const renderHeader = () => {
        if (props.property) {
            return (
                <>
                    {isApartment ? "Apartament" : getOfferTypeNameCapital(props.offer.type)}, {props.property.number}, {props.property.rooms}{" "}
                    {roomPlural(props.property.rooms)}, {areaFormat(props.property.area, {precision: 2})}
                </>
            );
        }

        return <span className="bt">{props.offer.name}</span>;
    };

    return (
        <nav className={cx(isNavBarSticky ? navigationWrapperStickyStyle : navigationWrapperStyle, props.className)} ref={wrapper}>
            <OneColumnGridHolder className={props.navBarContentStyle}>
                <OneColumnGridMain>
                    <nav className={isNavBarSticky ? navStyle : navJustifyRightStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_ACTIONS_SECTION}>
                        <div className={isNavBarSticky && !isMobile ? headerHolderVisibleStyle : headerHolderStyle}>
                            <Text as="h3" variant="headline_6">
                                {renderHeader()}
                            </Text>

                            <address className={addressHolderStyle}>
                                <MapMarkerLocationIcon className={addressIconStyle} fill="var(--colors-gray-700)" size="2.4" />
                                <Text color="var(--colors-gray-700)" variant="body_copy_2">
                                    {props.offer.address}
                                </Text>
                            </address>
                        </div>

                        <div className={buttonsWrapperStyle}>
                            <Hidden on="desktop">
                                {property && isPropertyFavouriteButtonAvailable(property) && (
                                    <FavoriteButton
                                        className={favoriteButtonStyle}
                                        offer={offer}
                                        property={property}
                                        display={{type: "button", size: "m"}}
                                        favouritePlacement="rp_favourite_property_nav"
                                        favouriteLocation="property_page"
                                    />
                                )}
                                {property && (
                                    <div className={fullWidthStyle}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <Button
                                                className={navBarButtonStyle}
                                                iconLeft={GalleryOutlineIcon}
                                                onClick={onShowPropertyGalleryClick}
                                                size="big"
                                                variant="brand_icon"
                                                dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.VIEW_GALLERY_BUTTON}
                                            >
                                                Zobacz galerię
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Hidden>
                            <Hidden on="mobile">
                                {property && property.plan && (
                                    <div className={fullWidthStyle}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <Button
                                                className={navBarButtonStyle}
                                                href={!planImage && !shouldOpenPlanUnlockChoice ? property.plan : undefined}
                                                iconLeft={BrandPropertyPlanIcon}
                                                onClick={planImage ? onShowPropertyPlanClick : onPlanDownloadClick}
                                                size="big"
                                                variant="brand_icon"
                                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_PLAN_BUTTON}
                                            >
                                                Zobacz rzut
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Hidden>

                            <div className={fullWidthStyle}>
                                <Button
                                    className={navBarButtonStyle}
                                    iconLeft={BrandMapPinIcon}
                                    onClick={onShowMapClick}
                                    size="big"
                                    variant="brand_icon"
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                                >
                                    Zobacz na mapie
                                </Button>
                            </div>
                        </div>
                    </nav>
                </OneColumnGridMain>
            </OneColumnGridHolder>
        </nav>
    );
});

const navigationWrapperBaseStyle = css.raw({
    display: "block",
    zIndex: 10,
    width: "100%",
    mt: 2,
    mr: 0,
    mb: 4,
    ml: 0,

    md: {
        mt: 2,
        mr: 0,
        mb: 6,
        ml: 0,
        position: "sticky",
        top: 0
    }
});

const navigationWrapperStyle = css(navigationWrapperBaseStyle, {
    md: {
        background: "transparent",
        boxShadow: "none"
    }
});

const navigationWrapperStickyStyle = css(navigationWrapperBaseStyle, {
    md: {
        background: "#fff",
        boxShadow: "0px 16px 60px rgba(174, 177, 180, 0.6)"
    }
});

const navBaseStyle = css.raw({
    width: "100%",
    display: "flex",
    alignItems: "center",

    md: {
        py: 2
    }
});

const navStyle = css(navBaseStyle, {
    justifyContent: "space-between"
});

const navJustifyRightStyle = css(navBaseStyle, {
    justifyContent: "flex-end"
});

const headerHolderStyle = css({
    opacity: 0,
    display: "none"
});

const headerHolderVisibleStyle = css({
    opacity: 0,
    display: "block",
    animationName: "fadeIn",
    animationDuration: "0.2s",
    animationTimingFunction: "cubic-bezier(0.39, 0.575, 0.565, 1)",
    animationFillMode: "both"
});

const addressHolderStyle = css({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    md: {
        mt: 1
    }
});

const addressIconStyle = css({
    mr: 2
});

const buttonsWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",

    md: {
        flexDirection: "row",
        width: "unset"
    },

    "& > div": {
        mb: 3,

        "&:last-of-type": {
            mb: 0
        },

        md: {
            ml: 3,
            mb: 0,

            "&:first-of-type": {
                ml: 0
            }
        }
    }
});

const favoriteButtonStyle = css({
    width: "100%",
    mb: 3
});

const fullWidthStyle = css({
    width: "100%"
});

const navBarButtonStyle = css({
    padding: 0,
    minWidth: "auto"
});
import React, {MouseEvent, useRef} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {BrandMapPinIcon, BrandPropertyPlanIcon, GalleryOutlineIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {UnlockChoiceCtaRequest, useUnlockChoiceCta} from "../../auth/hooks/use_unlock_choice_cta";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailOffer} from "../types/IPropertyDetailOffer";
import {isPropertyFavouriteButtonAvailable} from "../utils/is_property_favourite_button_available";

interface IProps {
    offer: IPropertyDetailOffer;
    property?: IPropertyDetail;
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    className?: string;
    navBarContentStyle?: string;
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyNavBar = React.memo((props: IProps) => {
    const {property, offer, onButtonClick} = props;

    const {isMobile} = useUserDevice();

    const wrapper = useRef<HTMLTableSectionElement>(null);
    const isNavBarSticky = useStickyIntersection(wrapper);

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const planImage = head(property?.plan_image_pages || [])?.image_url;

    const {isInterceptable, startUnlockChoice} = useUnlockChoiceCta();
    const planUnlockChoiceRequest: UnlockChoiceCtaRequest = {kind: "floor_plan", propertyId: property?.id};
    const shouldOpenPlanUnlockChoice = !planImage && isInterceptable(planUnlockChoiceRequest);

    const onShowPropertyGalleryClick = () => {
        props.onButtonClick(OfferModalActiveTab.GALLERY, GTMEventApplicationSourceSection.BUTTON);
    };

    const onShowPropertyPlanClick = () => {
        showPropertyPlanView(ShowPropertyPlanView.PROPERTY_DETAIL, offer, offer.vendor);
        if (property) {
            trackAmplitudeButtonClick({
                buttonName: "view_floor_plan",
                value: "see_floor_plan",
                buttonCategory: "property_page",
                itemId: offer.id,
                propertyId: property.id
            });
        }
        props.onButtonClick(
            OfferModalActiveTab.PLAN,
            isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
        );
    };

    const onPlanDownloadClick = (event: MouseEvent<HTMLElement>) => {
        if (startUnlockChoice(planUnlockChoiceRequest)) {
            event.preventDefault();
        }
    };

    const onShowMapClick = () => {
        if (property) {
            trackAmplitudeButtonClick({
                buttonName: "view_map",
                value: "see_on_map",
                buttonCategory: "property_page",
                itemId: offer.id,
                propertyId: property.id
            });
        }
        onButtonClick(OfferModalActiveTab.MAP, isNavBarSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON);
    };

    const renderHeader = () => {
        if (props.property) {
            return (
                <>
                    {isApartment ? "Apartament" : getOfferTypeNameCapital(props.offer.type)}, {props.property.number}, {props.property.rooms}{" "}
                    {roomPlural(props.property.rooms)}, {areaFormat(props.property.area, {precision: 2})}
                </>
            );
        }

        return <span className="bt">{props.offer.name}</span>;
    };

    return (
        <nav className={cx(isNavBarSticky ? navigationWrapperStickyStyle : navigationWrapperStyle, props.className)} ref={wrapper}>
            <OneColumnGridHolder className={props.navBarContentStyle}>
                <OneColumnGridMain>
                    <nav className={isNavBarSticky ? navStyle : navJustifyRightStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_ACTIONS_SECTION}>
                        <div className={isNavBarSticky && !isMobile ? headerHolderVisibleStyle : headerHolderStyle}>
                            <Text as="h3" variant="headline_6">
                                {renderHeader()}
                            </Text>

                            <address className={addressHolderStyle}>
                                <MapMarkerLocationIcon className={addressIconStyle} fill="var(--colors-gray-700)" size="2.4" />
                                <Text color="var(--colors-gray-700)" variant="body_copy_2">
                                    {props.offer.address}
                                </Text>
                            </address>
                        </div>

                        <div className={buttonsWrapperStyle}>
                            <Hidden on="desktop">
                                {property && isPropertyFavouriteButtonAvailable(property) && (
                                    <FavoriteButton
                                        className={favoriteButtonStyle}
                                        offer={offer}
                                        property={property}
                                        display={{type: "button", size: "m"}}
                                        favouritePlacement="rp_favourite_property_nav"
                                        favouriteLocation="property_page"
                                    />
                                )}
                                {property && (
                                    <div className={fullWidthStyle}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <Button
                                                className={navBarButtonStyle}
                                                iconLeft={GalleryOutlineIcon}
                                                onClick={onShowPropertyGalleryClick}
                                                size="big"
                                                variant="brand_icon"
                                                dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.VIEW_GALLERY_BUTTON}
                                            >
                                                Zobacz galerię
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Hidden>
                            <Hidden on="mobile">
                                {property && property.plan && (
                                    <div className={fullWidthStyle}>
                                        <div data-gtm-click="Rzut - nawigacja">
                                            <Button
                                                className={navBarButtonStyle}
                                                href={!planImage && !shouldOpenPlanUnlockChoice ? property.plan : undefined}
                                                iconLeft={BrandPropertyPlanIcon}
                                                onClick={planImage ? onShowPropertyPlanClick : onPlanDownloadClick}
                                                size="big"
                                                variant="brand_icon"
                                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_PLAN_BUTTON}
                                            >
                                                Zobacz rzut
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Hidden>

                            <div className={fullWidthStyle}>
                                <Button
                                    className={navBarButtonStyle}
                                    iconLeft={BrandMapPinIcon}
                                    onClick={onShowMapClick}
                                    size="big"
                                    variant="brand_icon"
                                    dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                                >
                                    Zobacz na mapie
                                </Button>
                            </div>
                        </div>
                    </nav>
                </OneColumnGridMain>
            </OneColumnGridHolder>
        </nav>
    );
});

const navigationWrapperBaseStyle = css.raw({
    display: "block",
    zIndex: 10,
    width: "100%",
    mt: 2,
    mr: 0,
    mb: 4,
    ml: 0,

    md: {
        mt: 2,
        mr: 0,
        mb: 6,
        ml: 0,
        position: "sticky",
        top: 0
    }
});

const navigationWrapperStyle = css(navigationWrapperBaseStyle, {
    md: {
        background: "transparent",
        boxShadow: "none"
    }
});

const navigationWrapperStickyStyle = css(navigationWrapperBaseStyle, {
    md: {
        background: "#fff",
        boxShadow: "0px 16px 60px rgba(174, 177, 180, 0.6)"
    }
});

const navBaseStyle = css.raw({
    width: "100%",
    display: "flex",
    alignItems: "center",

    md: {
        py: 2
    }
});

const navStyle = css(navBaseStyle, {
    justifyContent: "space-between"
});

const navJustifyRightStyle = css(navBaseStyle, {
    justifyContent: "flex-end"
});

const headerHolderStyle = css({
    opacity: 0,
    display: "none"
});

const headerHolderVisibleStyle = css({
    opacity: 0,
    display: "block",
    animationName: "fadeIn",
    animationDuration: "0.2s",
    animationTimingFunction: "cubic-bezier(0.39, 0.575, 0.565, 1)",
    animationFillMode: "both"
});

const addressHolderStyle = css({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    md: {
        mt: 1
    }
});

const addressIconStyle = css({
    mr: 2
});

const buttonsWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",

    md: {
        flexDirection: "row",
        width: "unset"
    },

    "& > div": {
        mb: 3,

        "&:last-of-type": {
            mb: 0
        },

        md: {
            ml: 3,
            mb: 0,

            "&:first-of-type": {
                ml: 0
            }
        }
    }
});

const favoriteButtonStyle = css({
    width: "100%",
    mb: 3
});

const fullWidthStyle = css({
    width: "100%"
});

const navBarButtonStyle = css({
    padding: 0,
    minWidth: "auto"
});
